// On-demand image loading for assets that are intentionally NOT preloaded by
// BootScene (portal initial-download budget). Callers build their UI with
// whatever fallback is available and rebuild when the download lands.
import Phaser from "phaser";

/** Downloads in flight, keyed by texture key. Textures are global but loaders
 *  are per-scene, so without this two scenes warming the same asset (MenuScene
 *  prefetching the campaign atlas, MapSelectScene opening onto it) would queue
 *  the same file twice and race each other's texture add. */
const inFlight = new Map<string, Promise<void>>();

/**
 * Queue every entry whose texture key is still missing on the scene's loader
 * and resolve once the loader drains. A failed download resolves too — the
 * texture stays missing and callers keep their fallback (same contract as
 * BootScene's tolerated loaderror handler). An entry another scene is already
 * downloading is awaited rather than re-requested.
 */
export function ensureImages(
  scene: Phaser.Scene,
  entries: ReadonlyArray<{ key: string; url: string }>
): Promise<void> {
  const missing = entries.filter(e => !scene.textures.exists(e.key));
  if (!missing.length) return Promise.resolve();
  return Promise.all(missing.map(e => ensureImage(scene, e))).then(() => undefined);
}

function ensureImage(scene: Phaser.Scene, entry: { key: string; url: string }): Promise<void> {
  const pending = inFlight.get(entry.key);
  if (pending) return pending;
  const p = new Promise<void>(resolve => {
    scene.load.image(entry.key, entry.url);
    scene.load.once(Phaser.Loader.Events.COMPLETE, () => {
      inFlight.delete(entry.key);
      resolve();
    });
    scene.load.start();
  });
  inFlight.set(entry.key, p);
  return p;
}
